import gsap from 'gsap';
import useSuspend from './useSuspend';

/**
 * Creates `beforeEnter`, `enter`, and `leave` transitions for use on a Vue <transition> tag.
 *
 * Accepts a `props` object to configure the animation parameters.
 *
 * The optional `vars` object can include `from` and `to` objects for the GSAP tweens.
 *
 * @param props
 * @param vars
 */
export default function useAnimatedTransition(
  props: {
    duration?: number;
    delay?: number;
    rotate?: number;
    scroll?: boolean;
  },
  { from = {}, to = {} }: { from?: GSAPTweenVars; to?: GSAPTweenVars } = {}
) {
  const suspend = useSuspend();

  const beforeEnter = (el: HTMLElement) => {
    gsap.set(el, {
      opacity: 0,
      pointerEvents: 'none',
      rotate: props.rotate,
      ...from
    });
  };

  const enter = (el: HTMLElement, done: GSAPCallback) => {
    const duration = suspend.reducedMotion ? 0 : props.duration;
    const delay = suspend.reducedMotion ? 0 : props.delay;

    gsap
      .timeline({
        onComplete: () => {
          if (props.scroll && !suspend.reducedMotion) {
            const { top, height } = el.getBoundingClientRect();

            if (
              // it could fit
              height < window.innerHeight &&
              // but it doesnt
              top + height > window.innerHeight &&
              // and the top isnt already higher than where we would want it
              top > window.innerHeight / 3
            ) {
              gsap.to(el.closest('.scroller'), {
                scrollTo: { y: el, offsetY: window.innerHeight / 3 },
                duration: 1,
                overwrite: true
              });
            }
          }

          done();
        }
      })
      .to(
        el,
        {
          scale: 1,
          duration,
          rotate: 0,
          ...to
        },
        delay
      )
      .to(
        el,
        {
          opacity: 1,
          duration,
          ease: 'linear',
          clearProps: true
        },
        delay
      )
      .set(el, { clearProps: true });
  };

  const leave = (el: HTMLElement, done: GSAPCallback) => {
    gsap.to(el, {
      opacity: 0,
      rotate: suspend.reducedMotion ? 0 : props.rotate,
      duration: suspend.reducedMotion ? 0 : undefined,
      onComplete: done,
      ...from
    });
  };

  return { beforeEnter, leave, enter };
}
